// src/components/notebook/MissReasonPanel.tsx
'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { NotebookPen } from 'lucide-react'
import type { ConfidenceRating } from '@unlimitedtests/core'
import { cn } from '@/lib/utils'
import {
  MISS_REASONS,
  MISS_REASON_HINT,
  MISS_REASON_LABEL,
  NOTEBOOK_HREF,
  RULE_MAX,
  parseNotebookEntry,
  type MissReason,
  type NotebookEntry,
} from '@/lib/notebook/notebook'

/**
 * "Why did you miss it?" — shown after a wrong answer (practice, the review
 * queue, a test's review), and as the editor on the notebook page.
 *
 * One tap on a reason saves the question to the student's mistake notebook
 * (src/lib/notebook/notebook.ts); the optional rule saves on Enter, on the
 * Save button, or when the field loses focus. Every save is a PATCH that only
 * sends the field that changed, so a reason tap never wipes a stored rule.
 *
 * Surfaces:
 *   paper   fixed light colours, for the answer/review panels that render as
 *           light "exam paper" whatever the theme (no theme tokens, no dark:)
 *   themed  the app's tokens, for ordinary pages (the notebook, test review)
 *
 * Confidence (src/lib/practice/confidence.ts): when the student rated the miss
 * before seeing it, the panel uses it. GUESS fills in the reason GUESS and
 * saves it — they already told us — unless the question already has a reason.
 * CERTAIN only SUGGESTS "Concept gap" (marked, not saved): being sure and wrong
 * usually means a wrong rule, but it can just as well be a misread. Either way
 * one tap changes it.
 */
export interface MissReasonPanelProps {
  questionId: string
  /** The stored entry when the caller already has it (null = not saved). Left
   *  undefined, the panel fetches it so a question already in the notebook
   *  shows its reason and rule. */
  initial?: NotebookEntry | null
  surface?: 'paper' | 'themed'
  /** "Open notebook" after a save. Off inside a running session. */
  showNotebookLink?: boolean
  /** Heading; the notebook page uses its own wording. */
  title?: string
  /** Show the rule field before a reason is picked. Off, the panel is one row
   *  of reasons until the first tap — compact under a wrong answer. */
  alwaysShowRule?: boolean
  onSaved?: (entry: NotebookEntry) => void
  className?: string
  /** The rating given before this (wrong) answer, if any. */
  confidence?: ConfidenceRating | null
}

const STYLES = {
  paper: {
    box: 'border-slate-300 bg-white text-slate-800',
    sub: 'text-slate-600',
    chip: 'border-slate-300 bg-slate-50 text-slate-800 hover:bg-slate-100',
    chipOn: 'border-emerald-600 bg-emerald-100 text-emerald-900',
    chipSuggested: 'border-dashed border-emerald-600',
    input:
      'border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus-visible:ring-emerald-600',
    button: 'bg-emerald-700 text-white hover:bg-emerald-800',
    ok: 'text-emerald-800',
    err: 'text-red-700',
    link: 'text-emerald-800',
  },
  themed: {
    box: 'border-border bg-card/60 text-foreground',
    sub: 'text-muted-foreground',
    chip: 'border-border bg-transparent text-muted-foreground hover:bg-muted/60 hover:text-foreground',
    chipOn: 'border-brand bg-brand/10 text-brand-ink',
    chipSuggested: 'border-dashed border-brand',
    input:
      'border-border bg-card/60 text-foreground placeholder:text-muted-foreground focus-visible:ring-brand/60',
    button: 'bg-brand text-on-brand hover:bg-brand/90',
    ok: 'text-brand-ink',
    err: 'text-red-700 dark:text-red-300',
    link: 'text-brand-ink',
  },
} as const

const EMPTY: NotebookEntry = { missReason: null, rule: null }

export default function MissReasonPanel({
  questionId,
  initial,
  surface = 'paper',
  showNotebookLink = true,
  title = 'Why did you miss it?',
  alwaysShowRule = false,
  onSaved,
  className,
  confidence = null,
}: MissReasonPanelProps) {
  const s = STYLES[surface]
  const ids = useId()
  const ruleId = `${ids}-rule`
  const headingId = `${ids}-heading`

  const [entry, setEntry] = useState<NotebookEntry>(initial ?? EMPTY)
  const [ruleDraft, setRuleDraft] = useState(initial?.rule ?? '')
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  // The last rule the server holds — the blur save only fires on a change.
  const savedRule = useRef(initial?.rule ?? '')
  // Once the student has tapped or typed, a late fetch must not overwrite it.
  const touched = useRef(false)

  // Only when the caller didn't hand us the stored entry.
  const needsFetch = initial === undefined
  // The stored entry is known (handed in, or the fetch settled) — the GUESS
  // prefill waits for it so it never overwrites a reason already saved.
  const [loaded, setLoaded] = useState(!needsFetch)
  useEffect(() => {
    if (!needsFetch) return
    const ctrl = new AbortController()
    fetch(`/api/bookmarks/${encodeURIComponent(questionId)}`, {
      cache: 'no-store',
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { entry?: unknown } | null) => {
        if (ctrl.signal.aborted || touched.current) return
        const e = parseNotebookEntry(data?.entry)
        if (!e) return
        setEntry(e)
        setRuleDraft(e.rule ?? '')
        savedRule.current = e.rule ?? ''
      })
      .catch(() => {
        // Best effort: an empty panel still saves.
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoaded(true)
      })
    return () => ctrl.abort()
  }, [needsFetch, questionId])

  // Saves run one after another, in tap order: a rule saved on blur and the
  // reason chip that caused the blur must both land.
  const queue = useRef<Promise<void>>(Promise.resolve())

  const send = useCallback(
    async (patch: Partial<NotebookEntry>, revert: NotebookEntry, prevRule: string) => {
      setStatus('saving')
      setError(null)
      try {
        const res = await fetch(`/api/bookmarks/${encodeURIComponent(questionId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify(patch),
        })
        const data = (await res.json().catch(() => ({}))) as { entry?: unknown; error?: unknown }
        const saved = res.ok ? parseNotebookEntry(data.entry) : null
        if (!saved) {
          throw new Error(
            typeof data.error === 'string' ? data.error : `Couldn't save (${res.status})`,
          )
        }
        setEntry(saved)
        savedRule.current = saved.rule ?? ''
        setStatus('saved')
        onSaved?.(saved)
      } catch (e) {
        setEntry(revert)
        savedRule.current = prevRule
        setStatus('error')
        setError(e instanceof Error ? e.message : "Couldn't save")
      }
    },
    [questionId, onSaved],
  )

  function save(patch: Partial<NotebookEntry>, revert: NotebookEntry) {
    const prevRule = savedRule.current
    queue.current = queue.current.then(() => send(patch, revert, prevRule))
  }

  // GUESS → the reason is GUESS, saved once, only onto an entry with no reason.
  const prefilled = useRef(false)
  const [prefilledGuess, setPrefilledGuess] = useState(false)
  useEffect(() => {
    if (confidence !== 'GUESS' || !loaded || prefilled.current || touched.current) return
    prefilled.current = true
    if (entry.missReason !== null) return
    setPrefilledGuess(true)
    setEntry({ ...entry, missReason: 'GUESS' })
    save({ missReason: 'GUESS' }, entry)
    // save/entry are read at the moment the prefill fires; it fires once.
  }, [confidence, loaded])
  // CERTAIN → "Concept gap" is suggested (marked), never saved for them.
  const suggested: MissReason | null =
    confidence === 'CERTAIN' && entry.missReason === null ? 'CONCEPT_GAP' : null

  function pickReason(reason: MissReason) {
    setPrefilledGuess(false)
    touched.current = true
    // Tapping the chosen reason again clears it.
    const next = entry.missReason === reason ? null : reason
    const before = entry
    setEntry({ ...entry, missReason: next })
    save({ missReason: next }, before)
  }

  function saveRule() {
    const draft = ruleDraft.replace(/\s+/g, ' ').trim()
    if (draft === savedRule.current.trim()) return
    const before = entry
    // Claimed now, so Enter followed by the blur it causes saves once.
    savedRule.current = draft
    save({ rule: draft.length > 0 ? draft : null }, before)
  }

  const inNotebook = entry.missReason !== null || entry.rule !== null
  const showRule = alwaysShowRule || inNotebook || ruleDraft.length > 0

  return (
    <section
      aria-labelledby={headingId}
      data-testid="miss-reason-panel"
      className={cn('rounded-md border p-3 text-sm', s.box, className)}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
        <h3 id={headingId} className="flex items-center gap-1.5 font-medium">
          <NotebookPen className="h-4 w-4 shrink-0" aria-hidden="true" />
          {title}
        </h3>
        <span className={cn('text-xs', s.sub)}>Saves it to your mistake notebook</span>
      </div>

      {suggested && (
        <p data-testid="miss-reason-suggestion" className={cn('mt-1 text-xs', s.sub)}>
          You were sure of this one — that&apos;s often a concept gap. Tap it if that&apos;s right.
        </p>
      )}

      <div role="group" aria-labelledby={headingId} className="mt-2 flex flex-wrap gap-1.5">
        {MISS_REASONS.map((r) => {
          const on = entry.missReason === r
          const isSuggested = suggested === r
          return (
            <button
              key={r}
              type="button"
              aria-pressed={on}
              aria-describedby={isSuggested ? `${ids}-suggested` : undefined}
              title={MISS_REASON_HINT[r]}
              onClick={() => pickReason(r)}
              data-suggested={isSuggested || undefined}
              className={cn(
                'min-h-[36px] rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                on ? s.chipOn : s.chip,
                isSuggested && s.chipSuggested,
              )}
            >
              {MISS_REASON_LABEL[r]}
              {isSuggested && (
                <span id={`${ids}-suggested`} className="ml-1 font-normal">
                  · suggested
                </span>
              )}
            </button>
          )
        })}
      </div>

      {showRule && (
        <form
          className="mt-2 flex flex-col gap-1.5 sm:flex-row sm:items-center"
          onSubmit={(e) => {
            e.preventDefault()
            saveRule()
          }}
        >
          <label htmlFor={ruleId} className="sr-only">
            Rule to remember (optional)
          </label>
          <input
            id={ruleId}
            type="text"
            value={ruleDraft}
            maxLength={RULE_MAX}
            onChange={(e) => {
              touched.current = true
              setRuleDraft(e.target.value)
            }}
            onBlur={saveRule}
            placeholder="Rule to remember (optional)"
            className={cn(
              'min-h-[40px] w-full flex-1 rounded-md border px-3 py-1.5 text-sm',
              'focus-visible:outline-none focus-visible:ring-2',
              s.input,
            )}
          />
          <button
            type="submit"
            className={cn(
              'min-h-[40px] shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
              s.button,
            )}
          >
            Save rule
          </button>
        </form>
      )}

      {ruleDraft.length > RULE_MAX - 30 && (
        <p className={cn('mt-1 text-right text-xs', s.sub)}>
          {ruleDraft.length}/{RULE_MAX}
        </p>
      )}

      <p role="status" aria-live="polite" className="mt-1.5 text-xs empty:mt-0">
        {status === 'saving' && <span className={s.sub}>Saving…</span>}
        {status === 'saved' && (
          <span className={s.ok}>
            {prefilledGuess && entry.missReason === 'GUESS'
              ? 'Saved as a guess, since you marked it one. Tap another reason to change it.'
              : inNotebook
                ? 'Saved to your notebook.'
                : 'Saved.'}
            {showNotebookLink && inNotebook && (
              <>
                {' '}
                <Link
                  href={NOTEBOOK_HREF}
                  className={cn('font-medium underline underline-offset-2', s.link)}
                >
                  Open notebook
                </Link>
              </>
            )}
          </span>
        )}
        {status === 'error' && <span className={s.err}>{error}</span>}
      </p>
    </section>
  )
}
